import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import Countdown from "@/components/Countdown";

const TABS = [
  { id: "active",  label: "Actifs",   icon: Ticket },
  { id: "used",    label: "Utilisés", icon: CheckCircle2 },
  { id: "expired", label: "Expirés",  icon: AlertTriangle },
];

function getVoucherState(v) {
  if (v.redeemed) return "used";
  if (v.expires_at && new Date(v.expires_at).getTime() < Date.now()) return "expired";
  return "active";
}

export default function MyVouchersPage() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");

  useEffect(() => {
    api.get("/vouchers/mine")
      .then(({ data }) => setVouchers(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const c = { active: 0, used: 0, expired: 0 };
    vouchers.forEach((v) => { c[getVoucherState(v)] += 1; });
    return c;
  }, [vouchers]);

  const filtered = useMemo(() => vouchers.filter((v) => getVoucherState(v) === tab), [vouchers, tab]);

  return (
    <div className="space-y-8" data-testid="vouchers-page">
      <header className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="phenix-eyebrow mb-3">Portefeuille</div>
          <h1 className="heading-display text-2xl lg:text-3xl text-plum-900">
            Mes <em className="text-pink-500">bons.</em>
          </h1>
          <p className="mt-6 text-base text-plum-700/70 max-w-xl leading-relaxed">
            Présentez le code à votre commerçant pour profiter de votre réduction.
            Vos bons expirent automatiquement après 90 jours.
          </p>
        </div>
        <div className="lg:col-span-4 flex items-end">
          <div className="border-t border-cream-200 pt-5 w-full">
            <div className="phenix-eyebrow mb-2">Disponibles</div>
            <div className="font-display text-5xl text-ink-900 leading-none">
              {counts.active}<span className="text-pink-500 text-2xl font-light">/{vouchers.length}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="vouchers-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          const count = counts[t.id] || 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`vouchers-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-[13px] font-bold border transition-colors whitespace-nowrap ${
                active
                  ? "bg-plum-700 text-white border-plum-700 shadow-md shadow-plum-700/15"
                  : "bg-white text-plum-700 border-cream-200 hover:bg-cream-50"
              }`}
            >
              <t.icon size={13} strokeWidth={2.4} />
              {t.label}
              <span className={`tabular-nums text-[11px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-cream-100 text-plum-700/60"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-plum-700/50 italic py-12 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-cream-200 p-16 text-center text-plum-700/50 italic rounded-2xl">
          {tab === "active" && "Vous n'avez aucun bon actif. Répondez à un sondage commerçant pour en gagner un."}
          {tab === "used" && "Aucun bon utilisé pour le moment."}
          {tab === "expired" && "Aucun bon expiré. Profitez-en pendant que c'est valide !"}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-px bg-ink-900/10 border border-cream-200">
          {filtered.map((v, i) => {
            const state = getVoucherState(v);
            return (
              <div
                key={v.id}
                data-testid="voucher-item"
                className={`bg-cream-50 p-8 lg:p-10 relative ${state !== "active" ? "opacity-60" : ""}`}
              >
                <div className="absolute left-0 top-0 bottom-0 w-px bg-clay-500" />
                <div className="flex items-start justify-between mb-8">
                  <div className="font-mono text-[11px] text-plum-700/50">
                    {String(i + 1).padStart(2, "0")} / {String(filtered.length).padStart(2, "0")}
                  </div>
                  <Ticket size={20} strokeWidth={1.25} className="text-pink-500" />
                </div>
                <div className="phenix-eyebrow text-pink-500 mb-3">Bon de réduction</div>
                <div className="font-display text-7xl text-ink-900 leading-none mb-2">
                  -{v.percent}<span className="font-light text-pink-500">%</span>
                </div>
                <p className="text-sm text-plum-700/70 italic mb-6">{v.label}</p>

                <div className="border-t border-cream-200 pt-5 space-y-3">
                  <div>
                    <div className="phenix-eyebrow mb-1">Commerce</div>
                    <div className="font-display text-xl text-ink-900">{v.merchant_name}</div>
                  </div>
                  <div>
                    <div className="phenix-eyebrow mb-1">Code</div>
                    <div className="font-mono text-2xl text-ink-900 tracking-wider" data-testid="voucher-code">
                      {v.code}
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between pt-3 flex-wrap gap-2">
                    <div className="text-[11px] text-plum-700/50 italic">
                      {v.expires_at ? (
                        <>Valide jusqu&apos;au {new Date(v.expires_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</>
                      ) : (
                        <>Sans date d&apos;expiration</>
                      )}
                    </div>
                    {state === "active" ? (
                      <Countdown until={v.expires_at} label="Expire dans" testid={`voucher-countdown-${v.id}`} />
                    ) : state === "used" ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-mint-50 border border-mint-200 text-mint-600 text-[11px] font-bold">
                        <CheckCircle2 size={11} strokeWidth={2.3} /> Utilisé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cream-100 border border-cream-200 text-plum-700/50 text-[11px] font-bold">
                        <Clock size={11} strokeWidth={2.3} /> Expiré
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
